import axios from "@/shared/scripts/AutoRefreshAxios";
import store from "../store";
import { Classified } from "../interfaces/classified/Classified.interface";
import { ClassifiedDeactivationReason } from "../interfaces/ClassifiedDeactivationReason.interface";
import { minimizeClassified } from "@/shared/scripts/ValidateData";
import { AxiosError } from "axios";
import { ApiHeaderInterface } from "../interfaces/ApiHeader.interface";

const baseUrl: string = process.env.VUE_APP_CLASSIFIEDS_MANAGEMENT_URL || "";
const version = "1";

export function getClassified(classifiedId: string): Promise<Classified> {
    const uri: string = "/classifieds/" + classifiedId.toString();
    const method = "get";

    return axios({
        method: method,
        url: baseUrl + version + uri,
        headers: {
            "Accept": "application/json",
            "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
        }
    })
        .then(response => response.data)
        .catch(error => { throw new Error(error); });
}

export function getClassifiedId(classifiedId: string): Promise<boolean> {
    // get name of the current step

    // const classifiedId: number = store.getters["FormModule/getClassifiedId"];

    //method and endpoint change if classifiedId already exists
    const uri: string = "/classifieds/" + classifiedId.toString();
    const method = "get";

    return axios({
        method: method,
        url: baseUrl + version + uri,
        headers: {
            "Accept": "application/json",
            "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
        }
    }).then(res => {
        const classifiedData = res.data;
        if(classifiedData) {
            // This runs on every step navigation, overwriting the whole `property` object with API data.
            // If the sale-only enforcementMeasuresRegisterConsulted field is already present in the
            // store (loaded earlier or just selected by the user), keep that in-session value across
            // the overwrite; otherwise the user's radio selection (kept in the component's own state)
            // gets silently wiped from the store before they can save.
            const storeCertificates = store.getters["FormModule/getValue"]("property.certificates");
            const storeHasEnforcementValue = !!storeCertificates
                && Object.prototype.hasOwnProperty.call(storeCertificates, "enforcementMeasuresRegisterConsulted");
            const previousEnforcementValue = store.getters["FormModule/getValue"](
                "property.certificates.enforcementMeasuresRegisterConsulted"
            );

            store.commit("FormModule/setClassifiedId", classifiedData.id);
            store.commit("MyClassifiedsModule/setMyClassifiedSelected", classifiedData);
            store.dispatch("FormModule/addValue", {"path": "property", "value": classifiedData.property});
            store.dispatch("FormModule/addValue", {"path": "transaction", "value": classifiedData.transaction});
            if (classifiedData.media && classifiedData.media.externalVideo) {
                store.dispatch("FormModule/addValue", {"path": "media.externalVideo", "value": classifiedData.media.externalVideo});
            }

            if (storeHasEnforcementValue) {
                store.dispatch("FormModule/addValue", {
                    "path": "property.certificates.enforcementMeasuresRegisterConsulted",
                    "value": previousEnforcementValue
                });
            }
            if (classifiedData.publication){
                store.commit("FormModule/setPublication", classifiedData.publication);
            }
            if (classifiedData.firstPublicationDate) {
                store.commit("FormModule/setFirstPublicationDate", classifiedData.firstPublicationDate);
            }
            if (classifiedData.lastActionDate) {
                store.commit("FormModule/setLastSave", classifiedData.lastActionDate);
            }
            if (["ACTIVE", "INACTIVE", "EXPIRED"].includes(classifiedData.status)) {
                store.dispatch("FormModule/setIsEditionMode", true);
            } else {
                store.dispatch("FormModule/setIsEditionMode", false);
            }

            if(classifiedData.copyFromId) {
                store.dispatch("FormModule/setCopyFromId", classifiedData.copyFromId);
            }

            if (classifiedData.customerId) {
                // Commit the {id} placeholder so downstream code has the customer id immediately,
                // then eagerly load the full record. IO-276: the missing-name guard fails OPEN on a
                // placeholder — it cannot know a name it has never fetched — so this dispatch is
                // what makes the guard effective on upgrade/extend/edit flows, not a nice-to-have.
                store.commit("CustomersModule/setCurrentCustomer", {id: classifiedData.customerId});
                store.dispatch("CustomersModule/setCurrentCustomer", classifiedData.customerId)
                    .catch(() => { /* the guard's own one-shot hydration re-attempts this on click */ });
            }

            if(classifiedData.pictures) {
                store.commit("PicturesModule/setPictures", classifiedData.pictures);
            }

            if(classifiedData.property.type === "GARAGE") {
                store.dispatch("StepsWizard/remove", [ "step-interior", "step-exterior"]);
            }
            if(classifiedData.property.type === "LAND") {
                store.dispatch("StepsWizard/remove", [ "step-interior", "step-energy"]);
            }
        }
        return Promise.resolve(true);

    }).catch(error => {
        console.error(error);
        return Promise.resolve(false);
    });
}

export function getMyClassifieds(): Promise<Classified[]> {

    //method and endpoint change if classifiedId already exists
    const uri = "/classifieds";
    const method = "get";

    return axios({
        method: method,
        url: baseUrl + version + uri,
        headers: {
            "Accept": "application/json",
            "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
        }
    })
        .then(res => res.data ?? [] as Array<Classified>)
        .catch(error => {
            throw new Error(error);
        });
}

export function deactivateClassified(reason: ClassifiedDeactivationReason): Promise<boolean> {

    const classified: Classified = store.getters["MyClassifiedsModule/getClassifiedToDeactivate"];

    //method and endpoint change if classifiedId already exists
    const uri = "/classifieds/" + classified.id + "/deactivate";
    const method = "put";

    return axios({
        method: method,
        url: baseUrl + version + uri,
        headers: {
            "Accept": "application/json",
            "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
            "customerId": classified.customerId,
        },
        data: reason.type ? reason : {},
    }).then(() => {
        return true;

    }).catch((error: AxiosError) => {
        if (error?.response?.data?.errors) {
            store.commit("MyClassifiedsModule/setClassifiedApiError", error.response.data.errors);
        }
        console.error(error);
        return Promise.reject(error);
    });
}

export function deleteClassified(): Promise<boolean> {

    const classified: Classified = store.getters["MyClassifiedsModule/getClassifiedToDelete"];

    const uri = "/classifieds/" + classified.id;
    const method = "delete";

    const headers: ApiHeaderInterface = {
        "Accept": "application/json",
        "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
    };

    // Conditionally add customerId to headers if it exists
    if (classified.customerId) {
        headers.customerId = classified.customerId;
    }

    return axios({
        method: method,
        url: baseUrl + version + uri,
        headers: headers,
    }).then(() => {
        return true;
    }).catch(error => {
        console.error(error);
        throw new Error(error);
    });
}

export function duplicateClassified(classified: Classified): Promise<Classified|boolean> {

    if(!classified) {
        return Promise.resolve(false);
    }

    const uri = `/classifieds/${classified.id}/duplicate`;
    const method = "post";

    return axios({
        method: method,
        url: baseUrl + version + uri,
        headers: {
            "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
            "customerId": classified.customerId
        }
    }).then(res => {
        if(res.data) {
            return res.data as Classified;
        }
        return true;
    }).catch(error => {
        throw new Error(error);
    });
}

export async function toggleUnderOptionStatus(classifiedSubset: Classified): Promise<boolean> {
    const customerId: string = classifiedSubset.customerId;
    const classifiedId: string = classifiedSubset.id;
    const version = "1";

    // As a `PUT` can only be done with a full classified, let’s retrieve it.
    const completeClassified: Classified = await getClassified(classifiedSubset.id)
        .catch((e:AxiosError) => {
            console.error(e);
            return Promise.reject(e);
        });

    completeClassified.transaction.isUnderOption = !completeClassified.transaction.isUnderOption;

    // remove non relevant fields
    minimizeClassified(completeClassified);

    return axios({
        method: "put",
        url: baseUrl + version + "/classifieds/" + classifiedId,
        headers: {
            "customerId": customerId,
            "Accept": "application/json",
            "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
        },
        data : completeClassified,
    }).then(res => {
        if(res.data) {
            classifiedSubset.transaction.isUnderOption = completeClassified.transaction.isUnderOption;
            store.commit("MyClassifiedsModule/replaceClassified", classifiedSubset);
        }
        return Promise.resolve(classifiedSubset.transaction.isUnderOption);
    }).catch((e:AxiosError) => {
        console.error(e);
        return Promise.reject(e);
    });
}
